import { toaster } from '@/components/ui/toaster';
import { SUBSCRIPTION_PRICE_DROP_QUERY_KEY } from '@/hooks/queries/use-subscription-price-drop';
import api from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import {
  ErrorCode,
  type ApiResponse,
  type StartSubscriptionPriceDropResult,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

type StartSubscriptionPriceDropResponse = ApiResponse<StartSubscriptionPriceDropResult>;

/** Lets the success toast render before the page leaves for Shopify. */
const REDIRECT_DELAY_MS = 500;

/**
 * Generic codes whose shared wording would read wrong here, named in this
 * flow's own words. Every other code goes through the app-wide `errors.<CODE>`
 * lookup, which already covers the pending change, the currency mismatch and
 * the Shopify refusal.
 */
const ERROR_KEY_BY_CODE: Partial<Record<string, string>> = {
  [ErrorCode.E_FORBIDDEN]: 'settings.subscription.priceDrop.viewerHelper',
  [ErrorCode.E_SUBSCRIPTION_NOT_FOUND]: 'settings.subscription.priceDrop.start.noLongerAvailable',
};

const startSubscriptionPriceDrop = async (): Promise<StartSubscriptionPriceDropResponse> => {
  const response = await api.post<StartSubscriptionPriceDropResponse>(
    '/dashboard/subscription/price-drop'
  );

  if (!response.data.success || !response.data.data) {
    // Same shape as a rejected axios call, so the `errors.<CODE>` lookup still
    // matches on the rare 2xx body that says no.
    throw Object.assign(
      new Error(response.data.message || 'Failed to start the subscription price drop'),
      { response }
    );
  }

  return response.data;
};

const readErrorCode = (error: unknown): string | undefined =>
  (error as { response?: { data?: { code?: string } } })?.response?.data?.code;

/**
 * Ask Shopify for a replacement subscription at the catalogue price and send
 * the merchant to the page where they approve it. Same redirect pattern as
 * `use-start-success-fee-migration.ts`.
 */
export function useStartSubscriptionPriceDrop() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<StartSubscriptionPriceDropResponse, Error, void>({
    mutationFn: startSubscriptionPriceDrop,
    onSuccess: (response) => {
      const confirmationUrl = response.data?.confirmationUrl;
      if (!confirmationUrl) return;

      toaster.success({
        title: t('settings.subscription.priceDrop.start.success.title'),
        description: t('settings.subscription.priceDrop.start.success.description'),
      });

      setTimeout(() => {
        window.location.href = confirmationUrl;
      }, REDIRECT_DELAY_MS);
    },
    onError: (error) => {
      // A refusal means the card was showing something the backend no longer
      // agrees with (nothing left to lower, a change already pending): read it
      // again so the card follows.
      void queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_PRICE_DROP_QUERY_KEY });

      const scopedKey = ERROR_KEY_BY_CODE[readErrorCode(error) ?? ''];

      toaster.error({
        title: t('settings.subscription.priceDrop.start.error'),
        description: scopedKey
          ? t(scopedKey)
          : getApiErrorMessage(error, t, 'settings.subscription.priceDrop.start.error'),
      });
    },
  });
}
